import { Component, type ErrorInfo, type ReactNode } from 'react'
import { toUserFacingError } from '@/lib/errors'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, info)
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const safeError = toUserFacingError(
        this.state.error,
        'We could not load this page. Please try again.',
        'app/render',
      )
      return (
        this.props.fallback ?? (
          <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
            <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
            <p className="text-muted-foreground">{safeError.userMessage}</p>
            <p className="text-xs text-muted-foreground">Error code: {safeError.code}</p>
          </div>
        )
      )
    }
    return this.props.children
  }
}
